import React, { useState, useEffect, useMemo } from 'react';
import { Table, Button, message, Typography, Spin, Form, InputNumber, Alert, Space, Input, Progress, Tabs, Switch } from 'antd';
import { SearchOutlined, ClearOutlined, SaveOutlined, LoadingOutlined, RobotOutlined, CalculatorOutlined } from '@ant-design/icons';
import * as configService from '../services/configService';
import { useAuth } from '../context/AuthContext';
import { COMPLETE_PARAMETER_REGISTRY, getCategoryFromKey } from '../utils/parameterRegistry';
import AIModelSettings from '../components/AIModelSettings';
import CalculatorSettings from '../components/CalculatorSettings';

const { Title } = Typography;

// Lightweight function to get parameters for a specific category
const getParametersForCategory = (category) => {
  const categoryParameters = [];
  
  Object.entries(COMPLETE_PARAMETER_REGISTRY).forEach(([key, displayName]) => {
    const paramCategory = getCategoryFromKey(key);
    if (paramCategory === category) {
      categoryParameters.push({
        key,
        displayName,
        category: paramCategory,
        // Core scoring parameters
        ideal: null,
        sd: null,
        isScored: true,
        overall_weight: null,
        // S-F-R weights for all health systems
        cardiovascular_s: null,
        cardiovascular_f: null,
        cardiovascular_r: null,
        metabolic_s: null,
        metabolic_f: null,
        metabolic_r: null,
        neurological_s: null,
        neurological_f: null,
        neurological_r: null,
        immune_s: null,
        immune_f: null,
        immune_r: null,
        respiratory_s: null,
        respiratory_f: null,
        respiratory_r: null,
        filtration_s: null,
        filtration_f: null,
        filtration_r: null,
        musculoskeletal_s: null,
        musculoskeletal_f: null,
        musculoskeletal_r: null,
        sourceFile: null
      });
    }
  });
  
  return categoryParameters;
};

// Apply configuration data to parameters
const applyConfigToParameters = (parameters, rawConfigs) => {
  if (!rawConfigs || typeof rawConfigs !== 'object') {
    return parameters;
  }

  const configsArray = Object.entries(rawConfigs)
    .filter(([name, data]) => name && data)
    .map(([name, data]) => ({ name, data }));

  // Create a map for faster lookups
  const parameterMap = new Map();
  parameters.forEach(param => parameterMap.set(param.key, { ...param }));

  // Process scoring configs
  const scoringConfigs = configsArray.filter(c => c.name.startsWith('scoring_config_'));
  for (const config of scoringConfigs) {
    if (config.data.parameters && Array.isArray(config.data.parameters)) {
      config.data.parameters.forEach(param => {
        if (parameterMap.has(param.key)) {
          const existingParam = parameterMap.get(param.key);
          parameterMap.set(param.key, {
            ...existingParam,
            ideal: param.target_value || param.ideal || existingParam.ideal,
            sd: param.sd?.value || param.stdDeviation || existingParam.sd,
            isScored: param.isScored !== undefined ? param.isScored : existingParam.isScored,
            sourceFile: config.name
          });
        }
      });
    }
  }

  // Process parameter weights to get S-F-R values
  const weightConfigs = configsArray.filter(c => c.name.startsWith('parameter_weights_'));
  for (const config of weightConfigs) {
    if (config.data.parameters) {
      for (const paramKey in config.data.parameters) {
        if (parameterMap.has(paramKey)) {
          const weightData = config.data.parameters[paramKey];
          const existingParam = parameterMap.get(paramKey);

          // Extract S-F-R weights for each system
          const updatedParam = { ...existingParam };
          if (weightData.systems) {
            Object.entries(weightData.systems).forEach(([system, weights]) => {
              updatedParam[`${system}_s`] = weights.S;
              updatedParam[`${system}_f`] = weights.F;
              updatedParam[`${system}_r`] = weights.R;
            });
          }

          updatedParam.overall_weight = weightData.overall_clinical_significance_weight;
          updatedParam.sourceFile = config.name;
          parameterMap.set(paramKey, updatedParam);
        }
      }
    }
  }

  return Array.from(parameterMap.values());
};

// Validation rules for different parameter types
const getValidationRules = (dataIndex) => {
  const rules = [{ required: false }];

  switch (dataIndex) {
    case 'sd':
      rules.push({
        type: 'number',
        min: 0.1,
        max: 50,
        message: 'Standard deviation must be between 0.1 and 50'
      });
      break;
    case 'cardiovascular_s':
    case 'cardiovascular_f':
    case 'cardiovascular_r':
    case 'metabolic_s':
    case 'metabolic_f':
    case 'metabolic_r':
    case 'neurological_s':
    case 'neurological_f':
    case 'neurological_r':
    case 'immune_s':
    case 'immune_f':
    case 'immune_r':
    case 'respiratory_s':
    case 'respiratory_f':
    case 'respiratory_r':
    case 'filtration_s':
    case 'filtration_f':
    case 'filtration_r':
    case 'musculoskeletal_s':
    case 'musculoskeletal_f':
    case 'musculoskeletal_r':
    case 'overall_weight':
      rules.push({
        type: 'number',
        min: 0,
        max: 100,
        message: 'Weight must be between 0% and 100%'
      });
      break;
    default:
      rules.push({
        type: 'number',
        message: 'Must be a valid number'
      });
  }

  return rules;
};

// Editable cell component
const EditableCell = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  handleCellSave,
  ...restProps
}) => {
  const [form] = Form.useForm();

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      handleCellSave(record.key, dataIndex, values[dataIndex]);
    } catch (errInfo) {
      console.log('Save failed:', errInfo);
    }
  };

  let inputNode;
  if (dataIndex === 'isScored') {
    inputNode = <Switch onChange={handleSave} />;
  } else {
    inputNode = <InputNumber step={0.1} onBlur={handleSave} onPressEnter={handleSave} />;
  }

  return (
    <td {...restProps}>
      {editing ? (
        <Form form={form} initialValues={{ [dataIndex]: record[dataIndex] }}>
          <Form.Item
            name={dataIndex}
            style={{ margin: 0 }}
            rules={dataIndex === 'isScored' ? [] : getValidationRules(dataIndex)}
            valuePropName={dataIndex === 'isScored' ? 'checked' : 'value'}
          >
            {inputNode}
          </Form.Item>
        </Form>
      ) : (
        children
      )}
    </td>
  );
};

const ClinicalSettingsPage = () => {
  const { user, token } = useAuth();
  const [tableData, setTableData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('Vitals');

  // Save operation state management
  const [saveOperation, setSaveOperation] = useState({
    isInProgress: false,
    currentStep: '',
    totalConfigs: 0,
    completedConfigs: 0,
    currentConfig: '',
    errors: []
  });

  // Filter data based on search term
  const filteredTableData = useMemo(() => {
    if (!searchTerm.trim()) {
      return tableData;
    }
    return tableData.filter(param => 
      param.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      param.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
      param.category.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [tableData, searchTerm]);

  // Handle search input changes
  const handleSearch = (value) => {
    setSearchTerm(value);
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm('');
  };

  // Load category data only when needed
  useEffect(() => {
    const loadCategoryData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get parameters for this category only
        const categoryParameters = getParametersForCategory(activeCategory);

        // Fetch config data for this category
        const rawConfigs = await configService.getConfigByCategory(activeCategory);

        // Apply config data to parameters
        const parametersWithConfig = applyConfigToParameters(categoryParameters, rawConfigs);

        setTableData([...parametersWithConfig]);
        setOriginalData([...parametersWithConfig]);

        console.log(`Loaded ${parametersWithConfig.length} parameters for category: ${activeCategory}`);
        setLoading(false);
      } catch (err) {
        console.error('loadCategoryData error:', err);
        const errorMessage = `Failed to load ${activeCategory} parameters: ${err.message || 'Unknown error'}`;
        setError(errorMessage);
        message.error(errorMessage);
        setLoading(false);
        setTableData([]);
        setOriginalData([]);
      }
    };

    loadCategoryData();
  }, [activeCategory]);

  const handleCellSave = (key, dataIndex, value) => {
    console.log(`[handleCellSave] Updating ${key}.${dataIndex} to ${value}`);

    // Create a deep copy to avoid mutating originalData
    const newData = tableData.map(item => {
      if (item.key === key) {
        // Create a new object with the updated value
        const oldValue = item[dataIndex];
        console.log(`[handleCellSave] Changed ${key}.${dataIndex} from ${oldValue} to ${value}`);
        return { ...item, [dataIndex]: value };
      }
      return item;
    });

    setTableData(newData);
  };

  const handleSave = async () => {
    setSaveOperation({
      isInProgress: true,
      currentStep: 'Analyzing changes...',
      totalConfigs: 0,
      completedConfigs: 0,
      currentConfig: '',
      errors: []
    });

    try {
      // Find all modified items by comparing current table data with original data
      setSaveOperation(prev => ({ ...prev, currentStep: 'Identifying modified parameters...' }));

      const modifiedItems = tableData.filter(currentItem => {
        const originalItem = originalData.find(o => o.key === currentItem.key);
        if (!originalItem) {
          console.log(`[handleSave] New item detected: ${currentItem.key}`);
          return true; // New item
        }

        // Compare key fields that are actually editable
        const isModified = (
          currentItem.isScored !== originalItem.isScored ||
          currentItem.ideal !== originalItem.ideal ||
          currentItem.sd !== originalItem.sd ||
          currentItem.overall_weight !== originalItem.overall_weight ||
          currentItem.cardiovascular_s !== originalItem.cardiovascular_s ||
          currentItem.cardiovascular_f !== originalItem.cardiovascular_f ||
          currentItem.cardiovascular_r !== originalItem.cardiovascular_r ||
          currentItem.metabolic_s !== originalItem.metabolic_s ||
          currentItem.metabolic_f !== originalItem.metabolic_f ||
          currentItem.metabolic_r !== originalItem.metabolic_r ||
          currentItem.neurological_s !== originalItem.neurological_s ||
          currentItem.neurological_f !== originalItem.neurological_f ||
          currentItem.neurological_r !== originalItem.neurological_r ||
          currentItem.immune_s !== originalItem.immune_s ||
          currentItem.immune_f !== originalItem.immune_f ||
          currentItem.immune_r !== originalItem.immune_r ||
          currentItem.respiratory_s !== originalItem.respiratory_s ||
          currentItem.respiratory_f !== originalItem.respiratory_f ||
          currentItem.respiratory_r !== originalItem.respiratory_r ||
          currentItem.filtration_s !== originalItem.filtration_s ||
          currentItem.filtration_f !== originalItem.filtration_f ||
          currentItem.filtration_r !== originalItem.filtration_r ||
          currentItem.musculoskeletal_s !== originalItem.musculoskeletal_s ||
          currentItem.musculoskeletal_f !== originalItem.musculoskeletal_f ||
          currentItem.musculoskeletal_r !== originalItem.musculoskeletal_r
        );

        if (isModified) {
          console.log(`[handleSave] Modified item detected: ${currentItem.key}`);
          // Log which fields changed
          if (currentItem.neurological_f !== originalItem.neurological_f) {
            console.log(`  - neurological_f: ${originalItem.neurological_f} → ${currentItem.neurological_f}`);
          }
          if (currentItem.cardiovascular_f !== originalItem.cardiovascular_f) {
            console.log(`  - cardiovascular_f: ${originalItem.cardiovascular_f} → ${currentItem.cardiovascular_f}`);
          }
        }

        return isModified;
      });

      console.log(`[handleSave] Found ${modifiedItems.length} modified items out of ${tableData.length} total`);
      console.log(`[handleSave] Original data count: ${originalData.length}`);

      if (modifiedItems.length === 0) {
        message.info({
          content: 'No changes to save.',
          duration: 4,
          key: 'noChanges'
        });
        setSaveOperation({ isInProgress: false, errors: [] });
        return;
      }

      // Group modified items by their source files
      setSaveOperation(prev => ({
        ...prev,
        currentStep: `Saving ${modifiedItems.length} modified parameters...`,
        totalConfigs: 2 // Typically scoring config + weights config
      }));

      // Get current configs for this category
      const rawConfigs = await configService.getConfigByCategory(activeCategory);

      // Update scoring config
      const scoringConfigName = `scoring_config_${activeCategory.toLowerCase()}`;
      if (rawConfigs[scoringConfigName]) {
        const updatedScoringConfig = { ...rawConfigs[scoringConfigName] };

        modifiedItems.forEach(item => {
          if (updatedScoringConfig.parameters) {
            const paramIndex = updatedScoringConfig.parameters.findIndex(p => p.key === item.key);
            if (paramIndex > -1) {
              updatedScoringConfig.parameters[paramIndex] = {
                ...updatedScoringConfig.parameters[paramIndex],
                target_value: item.ideal,
                sd: { value: item.sd, type: "absolute" },
                isScored: item.isScored
              };
            }
          }
        });

        await configService.updateConfig(scoringConfigName, updatedScoringConfig);
        setSaveOperation(prev => ({ ...prev, completedConfigs: 1 }));
      }

      // Update weights config
      const weightsConfigName = `parameter_weights_${activeCategory.toLowerCase()}`;
      if (rawConfigs[weightsConfigName]) {
        const updatedWeightsConfig = { ...rawConfigs[weightsConfigName] };

        modifiedItems.forEach(item => {
          if (!updatedWeightsConfig.parameters[item.key]) {
            updatedWeightsConfig.parameters[item.key] = {};
          }

          // Log what we're about to save for debugging
          console.log(`Updating ${item.key}:`, {
            neurological_f: item.neurological_f,
            cardiovascular_f: item.cardiovascular_f
          });

          updatedWeightsConfig.parameters[item.key] = {
            ...updatedWeightsConfig.parameters[item.key],
            overall_clinical_significance_weight: item.overall_weight,
            systems: {
              cardiovascular: { S: item.cardiovascular_s, F: item.cardiovascular_f, R: item.cardiovascular_r },
              metabolic: { S: item.metabolic_s, F: item.metabolic_f, R: item.metabolic_r },
              neurological: { S: item.neurological_s, F: item.neurological_f, R: item.neurological_r },
              immune: { S: item.immune_s, F: item.immune_f, R: item.immune_r },
              respiratory: { S: item.respiratory_s, F: item.respiratory_f, R: item.respiratory_r },
              filtration: { S: item.filtration_s, F: item.filtration_f, R: item.filtration_r },
              musculoskeletal: { S: item.musculoskeletal_s, F: item.musculoskeletal_f, R: item.musculoskeletal_r }
            }
          };
        });

        console.log(`Saving ${weightsConfigName} with ${modifiedItems.length} modified parameters`);
        const saveResponse = await configService.updateConfig(weightsConfigName, updatedWeightsConfig);
        console.log(`Save response for ${weightsConfigName}:`, saveResponse);
        setSaveOperation(prev => ({ ...prev, completedConfigs: 2 }));
      }

      message.success({
        content: `Successfully saved ${modifiedItems.length} parameter changes!`,
        duration: 4,
        key: 'saveSuccess'
      });

      // Reload data from server to ensure we have the latest saved values
      setSaveOperation(prev => ({ ...prev, currentStep: 'Reloading data from server...' }));

      try {
        const categoryParameters = getParametersForCategory(activeCategory);
        const reloadedConfigs = await configService.getConfigByCategory(activeCategory);
        const reloadedParameters = applyConfigToParameters(categoryParameters, reloadedConfigs);

        setTableData([...reloadedParameters]);
        setOriginalData([...reloadedParameters]);

        console.log(`Reloaded ${reloadedParameters.length} parameters after save`);
      } catch (reloadError) {
        console.error('Failed to reload data after save:', reloadError);
        message.warning({
          content: 'Changes saved but failed to reload. Please refresh the page.',
          duration: 6
        });
        // Still update originalData with tableData as fallback
        setOriginalData(JSON.parse(JSON.stringify(tableData)));
      }

    } catch (error) {
      console.error('Failed to save changes:', error);
      message.error({
        content: `Save failed: ${error.message || 'Unknown error'}`,
        duration: 8,
        key: 'saveError'
      });
    } finally {
      setSaveOperation({
        isInProgress: false,
        currentStep: '',
        totalConfigs: 0,
        completedConfigs: 0,
        currentConfig: '',
        errors: []
      });
    }
  };

  const columns = [
    // Parameter Identification - Fixed left columns
    {
      title: 'Parameter Name',
      dataIndex: 'displayName',
      width: 200,
      fixed: 'left',
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 500, fontSize: '13px' }}>{text}</div>
          <div style={{ fontSize: '11px', color: '#666' }}>{record.key}</div>
        </div>
      )
    },
    {
      title: 'Category',
      dataIndex: 'category',
      width: 100,
      fixed: 'left',
    },

    // Core Scoring Parameters
    {
      title: 'Scored',
      dataIndex: 'isScored',
      width: 80,
      editable: true,
      render: (value) => <Switch checked={value} size="small" />
    },
    {
      title: 'Ideal Target',
      dataIndex: 'ideal',
      width: 100,
      editable: true,
      render: (value) => value !== null && value !== undefined ? Number(value).toFixed(2) : '-'
    },
    {
      title: 'Std Dev',
      dataIndex: 'sd',
      width: 90,
      editable: true,
      render: (value) => value !== null && value !== undefined ? Number(value).toFixed(2) : '-'
    },
    {
      title: 'Overall Weight',
      dataIndex: 'overall_weight',
      width: 110,
      editable: true,
      render: (value) => value !== null && value !== undefined ? `${Number(value).toFixed(1)}%` : '-'
    },

    // S-F-R Weights - Cardiovascular System
    {
      title: 'Cardio S',
      dataIndex: 'cardiovascular_s',
      width: 80,
      editable: true,
      render: (value) => value !== null && value !== undefined ? `${Number(value).toFixed(0)}%` : '-'
    },
    {
      title: 'Cardio F',
      dataIndex: 'cardiovascular_f',
      width: 80,
      editable: true,
      render: (value) => value !== null && value !== undefined ? `${Number(value).toFixed(0)}%` : '-'
    },
    {
      title: 'Cardio R',
      dataIndex: 'cardiovascular_r',
      width: 80,
      editable: true,
      render: (value) => value !== null && value !== undefined ? `${Number(value).toFixed(0)}%` : '-'
    },

    // S-F-R Weights - Metabolic System
    {
      title: 'Metab S',
      dataIndex: 'metabolic_s',
      width: 80,
      editable: true,
      render: (value) => value !== null && value !== undefined ? `${Number(value).toFixed(0)}%` : '-'
    },
    {
      title: 'Metab F',
      dataIndex: 'metabolic_f',
      width: 80,
      editable: true,
      render: (value) => value !== null && value !== undefined ? `${Number(value).toFixed(0)}%` : '-'
    },
    {
      title: 'Metab R',
      dataIndex: 'metabolic_r',
      width: 80,
      editable: true,
      render: (value) => value !== null && value !== undefined ? `${Number(value).toFixed(0)}%` : '-'
    },

    // S-F-R Weights - Neurological System
    {
      title: 'Neuro S',
      dataIndex: 'neurological_s',
      width: 80,
      editable: true,
      render: (value) => value !== null && value !== undefined ? `${Number(value).toFixed(0)}%` : '-'
    },
    {
      title: 'Neuro F',
      dataIndex: 'neurological_f',
      width: 80,
      editable: true,
      render: (value) => value !== null && value !== undefined ? `${Number(value).toFixed(0)}%` : '-'
    },
    {
      title: 'Neuro R',
      dataIndex: 'neurological_r',
      width: 80,
      editable: true,
      render: (value) => value !== null && value !== undefined ? `${Number(value).toFixed(0)}%` : '-'
    },

    // S-F-R Weights - Immune System
    {
      title: 'Immune S',
      dataIndex: 'immune_s',
      width: 80,
      editable: true,
      render: (value) => value !== null && value !== undefined ? `${Number(value).toFixed(0)}%` : '-'
    },
    {
      title: 'Immune F',
      dataIndex: 'immune_f',
      width: 80,
      editable: true,
      render: (value) => value !== null && value !== undefined ? `${Number(value).toFixed(0)}%` : '-'
    },
    {
      title: 'Immune R',
      dataIndex: 'immune_r',
      width: 80,
      editable: true,
      render: (value) => value !== null && value !== undefined ? `${Number(value).toFixed(0)}%` : '-'
    },

    // S-F-R Weights - Respiratory System
    {
      title: 'Resp S',
      dataIndex: 'respiratory_s',
      width: 80,
      editable: true,
      render: (value) => value !== null && value !== undefined ? `${Number(value).toFixed(0)}%` : '-'
    },
    {
      title: 'Resp F',
      dataIndex: 'respiratory_f',
      width: 80,
      editable: true,
      render: (value) => value !== null && value !== undefined ? `${Number(value).toFixed(0)}%` : '-'
    },
    {
      title: 'Resp R',
      dataIndex: 'respiratory_r',
      width: 80,
      editable: true,
      render: (value) => value !== null && value !== undefined ? `${Number(value).toFixed(0)}%` : '-'
    },

    // S-F-R Weights - Filtration System
    {
      title: 'Filt S',
      dataIndex: 'filtration_s',
      width: 80,
      editable: true,
      render: (value) => value !== null && value !== undefined ? `${Number(value).toFixed(0)}%` : '-'
    },
    {
      title: 'Filt F',
      dataIndex: 'filtration_f',
      width: 80,
      editable: true,
      render: (value) => value !== null && value !== undefined ? `${Number(value).toFixed(0)}%` : '-'
    },
    {
      title: 'Filt R',
      dataIndex: 'filtration_r',
      width: 80,
      editable: true,
      render: (value) => value !== null && value !== undefined ? `${Number(value).toFixed(0)}%` : '-'
    },

    // S-F-R Weights - Musculoskeletal System
    {
      title: 'MSK S',
      dataIndex: 'musculoskeletal_s',
      width: 80,
      editable: true,
      render: (value) => value !== null && value !== undefined ? `${Number(value).toFixed(0)}%` : '-'
    },
    {
      title: 'MSK F',
      dataIndex: 'musculoskeletal_f',
      width: 80,
      editable: true,
      render: (value) => value !== null && value !== undefined ? `${Number(value).toFixed(0)}%` : '-'
    },
    {
      title: 'MSK R',
      dataIndex: 'musculoskeletal_r',
      width: 80,
      editable: true,
      render: (value) => value !== null && value !== undefined ? `${Number(value).toFixed(0)}%` : '-'
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        inputType: col.dataIndex === 'isScored' ? 'boolean' : 'number',
        dataIndex: col.dataIndex,
        title: col.title,
        editing: true, // Always editable
        handleCellSave,
      }),
    };
  });

  if (loading) {
    return <Spin tip="Loading Clinical Settings..." />;
  }

  // Check if user has admin role
  if (!user || user.role !== 'Admin') {
    return (
      <div>
        <Title level={2}>Clinical Scoring Configuration</Title>
        <Alert
          message="Access Denied"
          description={`Administrator privileges are required to access Clinical Settings. Your current role: ${user?.role || 'Unknown'}`}
          type="error"
          showIcon
          style={{ marginTop: 16 }}
        />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error Loading Clinical Settings"
        description={error}
        type="error"
        showIcon
      />
    );
  }

  // Handle tab change - only update activeCategory for valid category tabs
  const handleTabChange = (key) => {
    const validCategories = ['Vitals', 'Biometrics', 'Physical Exam', 'Labs', 'Imaging', 'Lifestyle'];
    if (validCategories.includes(key)) {
      setActiveCategory(key);
    }
  };

  return (
    <div>
      <Title level={2}>Clinical Settings</Title>
      <p>Configure clinical scoring parameters and AI model settings for the Care360 system.</p>

      <Tabs defaultActiveKey="Vitals" type="card" onChange={handleTabChange}>
        {['Vitals', 'Biometrics', 'Physical Exam', 'Labs', 'Imaging', 'Lifestyle'].map(category => (
          <Tabs.TabPane tab={category} key={category}>
            <div style={{ marginTop: 16 }}>
              <Title level={3}>Clinical Scoring Configuration</Title>
              <p>Edit the clinical weights, ideal targets, and other scoring parameters. Changes will be applied globally.</p>

              {/* Search Input */}
              <Space style={{ marginBottom: 16 }}>
                <Input
                  placeholder="Search parameters by name, key, or category..."
                  prefix={<SearchOutlined />}
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  style={{ width: 400 }}
                  allowClear
                  onClear={clearSearch}
                />
                <Button onClick={clearSearch} icon={<ClearOutlined />}>
                  Clear
                </Button>
                <span style={{ marginLeft: 16, color: '#666' }}>
                  Showing {filteredTableData.length} of {tableData.length} parameters
                </span>
              </Space>

              {/* Save All Changes Button with Progress Feedback */}
              <div style={{ marginBottom: 16 }}>
                <Button
                  onClick={handleSave}
                  type="primary"
                  loading={saveOperation.isInProgress}
                  disabled={saveOperation.isInProgress}
                  icon={saveOperation.isInProgress ? undefined : <SaveOutlined />}
                  size="large"
                  style={{
                    minWidth: 200,
                    height: 40
                  }}
                >
                  {saveOperation.isInProgress ? (
                    saveOperation.totalConfigs > 0 ?
                      `Saving ${saveOperation.completedConfigs}/${saveOperation.totalConfigs}` :
                      'Preparing...'
                  ) : (
                    'Save All Changes'
                  )}
                </Button>

                {/* Progress Information */}
                {saveOperation.isInProgress && (
                  <div style={{ marginTop: 8, fontSize: '14px', color: '#666' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <LoadingOutlined style={{ color: '#1890ff' }} />
                      <span>{saveOperation.currentStep}</span>
                    </div>
                    {saveOperation.currentConfig && (
                      <div style={{ marginTop: 4, fontSize: '12px', color: '#999' }}>
                        Current: {saveOperation.currentConfig}
                      </div>
                    )}
                    {saveOperation.totalConfigs > 0 && (
                      <div style={{ marginTop: 4 }}>
                        <Progress
                          percent={Math.round((saveOperation.completedConfigs / saveOperation.totalConfigs) * 100)}
                          size="small"
                          status={saveOperation.errors.length > 0 ? "exception" : "active"}
                          showInfo={false}
                          style={{ maxWidth: 300 }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              <Table
                components={{
                  body: {
                    cell: EditableCell,
                  },
                }}
                dataSource={filteredTableData}
                columns={mergedColumns}
                bordered
                rowKey="key"
                scroll={{ x: 2000, y: 600 }}
                pagination={{
                  pageSize: 50,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} parameters`
                }}
              />
            </div>
          </Tabs.TabPane>
        ))}
        <Tabs.TabPane
          tab={
            <span>
              <CalculatorOutlined />
              Calculators
            </span>
          }
          key="calculators"
        >
          <CalculatorSettings />
        </Tabs.TabPane>
        <Tabs.TabPane
          tab={
            <span>
              <RobotOutlined />
              AI Model Settings
            </span>
          }
          key="ai-settings"
        >
          <AIModelSettings />
        </Tabs.TabPane>
      </Tabs>
    </div>
  );
};

export default ClinicalSettingsPage;
